import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MapPin, Star, CheckCircle, Filter, X, Clock, Award, 
  Navigation, Loader2, Zap, ArrowUpDown, ChevronRight 
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const userIcon = new L.DivIcon({
  html: '<div style="width:18px;height:18px;background:#3B82F6;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 3px rgba(59,130,246,0.3),0 2px 8px rgba(0,0,0,0.3)"></div>',
  className: '',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const panditIcon = new L.DivIcon({
  html: '<div style="width:14px;height:14px;background:#EA580C;border:2.5px solid #fff;border-radius:50%;box-shadow:0 0 0 2px rgba(234,88,12,0.3),0 2px 6px rgba(0,0,0,0.25)"></div>',
  className: '',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const panditIconBusy = new L.DivIcon({
  html: '<div style="width:14px;height:14px;background:#94A3B8;border:2.5px solid #fff;border-radius:50%;box-shadow:0 0 0 2px rgba(148,163,184,0.3),0 2px 6px rgba(0,0,0,0.2)"></div>',
  className: '',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Map auto-fit component
function MapBounds({ pandits, userLoc }) {
  const map = useMap();
  useEffect(() => {
    if (!userLoc) return;
    const points = [[userLoc.lat, userLoc.lng]];
    pandits.forEach(p => { if (p.latitude && p.longitude) points.push([p.latitude, p.longitude]); });
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
    } else {
      map.setView([userLoc.lat, userLoc.lng], 12);
    }
  }, [pandits, userLoc, map]);
  return null;
}

export default function FindPanditPage({ user }) {
  const navigate = useNavigate();
  const listRef = useRef(null);

  // Location state
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, detecting, found, denied, manual
  const [userLocation, setUserLocation] = useState(null);
  const [cityName, setCityName] = useState('');
  const [manualCity, setManualCity] = useState('');

  // Nearby pandits
  const [nearbyPandits, setNearbyPandits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalFound, setTotalFound] = useState(0);
  const [sortBy, setSortBy] = useState('nearest');
  const [radius, setRadius] = useState(100);

  // Map
  const [showMap, setShowMap] = useState(true);
  const [selectedPandit, setSelectedPandit] = useState(null);

  // Fallback pandits (when no location)
  const [fallbackPandits, setFallbackPandits] = useState([]);

  // Categories
  const CATEGORIES = [
    { name: 'Griha Pravesh', icon: '🏠' },
    { name: 'Vivah/Wedding', icon: '💍' },
    { name: 'Satyanarayan Puja', icon: '🙏' },
    { name: 'Rudrabhishek', icon: '🔱' },
    { name: 'Mundan', icon: '👶' },
    { name: 'Navgraha Shanti', icon: '⭐' },
    { name: 'Ganesh Puja', icon: '🐘' },
    { name: 'Lakshmi Puja', icon: '🪷' },
    { name: 'Vastu Puja', icon: '🧭' },
    { name: 'Havan', icon: '🔥' },
  ];
  const [activeCategory, setActiveCategory] = useState('');

  // Fetch nearby pandits from new API
  const fetchNearbyPandits = useCallback(async (lat, lng, sort = sortBy, rad = radius) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/pandits/nearby`, {
        params: { lat, lng, radius: rad, sort, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data;
      let results = data.pandits || [];
      
      // Filter by category if active
      if (activeCategory) {
        results = results.filter(p => 
          (p.puja_types || []).some(t => t.toLowerCase().includes(activeCategory.toLowerCase()))
        );
      }
      
      setNearbyPandits(results);
      setTotalFound(results.length);
    } catch (err) {
      console.error('Nearby fetch failed:', err);
      toast.error('Failed to find nearby pandits');
    } finally {
      setLoading(false);
    }
  }, [sortBy, radius, activeCategory]);

  // Fetch fallback pandits (no location)
  const fetchFallbackPandits = useCallback(async (city = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const params = city ? `?city=${city}` : '?limit=20';
      const res = await axios.get(`${API}/pandit/profiles${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFallbackPandits(res.data || []);
      setTotalFound(res.data?.length || 0);
    } catch {
      toast.error('Failed to load pandits');
    } finally {
      setLoading(false);
    }
  }, []);

  // Detect location
  const detectLocation = useCallback(async () => {
    setLocationStatus('detecting');

    // Check cache
    const cached = sessionStorage.getItem('dharmbook_geo_full');
    if (cached) {
      try {
        const { lat, lng, city, state } = JSON.parse(cached);
        setUserLocation({ lat, lng });
        setCityName(city || state || '');
        setLocationStatus('found');
        fetchNearbyPandits(lat, lng);
        return;
      } catch {}
    }

    if (!navigator.geolocation) {
      setLocationStatus('denied');
      fetchFallbackPandits();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationStatus('found');
        
        // Reverse geocode for city name
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en`,
            { headers: { 'User-Agent': 'DharmBook/1.0' } }
          );
          const data = await res.json();
          const addr = data.address || {};
          const city = addr.city || addr.town || addr.village || addr.state_district || '';
          const state = addr.state || '';
          setCityName(city || state);
          sessionStorage.setItem('dharmbook_geo_full', JSON.stringify({ lat: latitude, lng: longitude, city, state }));
        } catch {}
        
        fetchNearbyPandits(latitude, longitude);
      },
      () => {
        setLocationStatus('denied');
        fetchFallbackPandits();
      },
      { timeout: 8000, maximumAge: 300000 }
    );
  }, [fetchNearbyPandits, fetchFallbackPandits]);

  useEffect(() => {
    detectLocation();
  }, []);

  // Re-fetch when sort/radius/category changes
  useEffect(() => {
    if (userLocation) {
      fetchNearbyPandits(userLocation.lat, userLocation.lng, sortBy, radius);
    }
  }, [sortBy, radius, activeCategory]);

  // Manual city search
  const handleManualSearch = () => {
    if (!manualCity.trim()) return;
    setCityName(manualCity.trim());
    setLocationStatus('manual');
    fetchFallbackPandits(manualCity.trim());
  };

  // Get the pandits list to display
  const displayPandits = locationStatus === 'found' ? nearbyPandits : fallbackPandits;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="find-pandit-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 text-white">
        <div className="max-w-5xl mx-auto px-4 py-6 pb-4">
          <h1 className="text-2xl sm:text-3xl font-bold mb-1">Find Pandit Ji</h1>
          <p className="text-orange-100 text-sm">Book verified pandits for all your spiritual ceremonies</p>
        </div>
      </div>

      {/* Location Bar */}
      <div className="bg-white border-b border-stone-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-3">
          {locationStatus === 'idle' || locationStatus === 'detecting' ? (
            <div className="flex items-center gap-3" data-testid="location-detecting">
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Navigation className="w-4 h-4 text-blue-500 animate-pulse" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Finding Pandit Ji near you...</p>
                <p className="text-xs text-slate-400">Detecting your location</p>
              </div>
              <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
            </div>
          ) : locationStatus === 'found' ? (
            <div className="flex items-center gap-3" data-testid="location-found">
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {cityName ? `Nearby in ${cityName}` : 'Your Location'}
                </p>
                <p className="text-xs text-slate-400">{totalFound} pandits available within {radius} km</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <select
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-orange-300"
                  data-testid="radius-select"
                >
                  <option value={25}>25 km</option>
                  <option value={50}>50 km</option>
                  <option value={100}>100 km</option>
                  <option value={250}>250 km</option>
                  <option value={500}>500 km</option>
                </select>
              </div>
            </div>
          ) : (
            <div data-testid="location-denied">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-orange-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">Enter your city</p>
                  <p className="text-xs text-slate-400">We'll find pandits near you</p>
                </div>
                <button
                  onClick={detectLocation}
                  className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 font-semibold rounded-lg hover:bg-blue-100 transition-colors"
                  data-testid="use-location-btn"
                >
                  Use My Location
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualCity}
                  onChange={(e) => setManualCity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                  placeholder="Enter city name (e.g., Delhi, Mumbai...)"
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300"
                  data-testid="manual-city-input"
                />
                <button
                  onClick={handleManualSearch}
                  className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-xl hover:bg-orange-600 transition-colors"
                  data-testid="search-city-btn"
                >
                  Search
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map Section */}
      {locationStatus === 'found' && showMap && userLocation && (
        <div className="relative" data-testid="pandit-map">
          <div style={{ height: '260px' }} className="w-full">
            <MapContainer
              center={[userLocation.lat, userLocation.lng]}
              zoom={11}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapBounds pandits={nearbyPandits} userLoc={userLocation} />

              {/* User marker */}
              <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
                <Popup><span className="text-xs font-semibold">You are here</span></Popup>
              </Marker>
              
              {/* User radius circle */}
              <Circle
                center={[userLocation.lat, userLocation.lng]}
                radius={radius * 1000}
                pathOptions={{ color: '#EA580C', fillColor: '#FED7AA', fillOpacity: 0.08, weight: 1, dashArray: '6 4' }}
              />

              {/* Pandit markers */}
              {nearbyPandits.map(p => (
                <Marker
                  key={p.id}
                  position={[p.latitude, p.longitude]}
                  icon={p.availability === 'busy' ? panditIconBusy : panditIcon}
                  eventHandlers={{
                    click: () => {
                      setSelectedPandit(p);
                      const el = document.getElementById(`pandit-card-${p.id}`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                >
                  <Popup>
                    <div className="text-xs min-w-[160px]">
                      <p className="font-bold text-slate-800">{p.name}</p>
                      <p className="text-slate-500">{p.city} &bull; {p.distance_km} km</p>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-yellow-600">{p.rating}</span>
                        <span className="text-slate-400">&bull;</span>
                        <span className={p.availability === 'available' ? 'text-green-600' : 'text-slate-400'}>
                          {p.availability === 'available' ? 'Available' : 'Busy'}
                        </span>
                      </div>
                      <button
                        onClick={() => navigate(`/pandit/${p.id}`)}
                        className="mt-2 w-full text-center py-1 bg-orange-500 text-white rounded text-[11px] font-semibold"
                      >
                        View Profile
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          {/* Map toggle */}
          <button
            onClick={() => setShowMap(false)}
            className="absolute bottom-3 right-3 z-[1000] px-3 py-1.5 bg-white shadow-lg rounded-full text-xs font-medium text-slate-600 hover:bg-slate-50 border border-slate-200"
          >
            Hide Map
          </button>
        </div>
      )}

      {/* Show Map button when hidden */}
      {locationStatus === 'found' && !showMap && (
        <div className="max-w-5xl mx-auto px-4 mt-3">
          <button
            onClick={() => setShowMap(true)}
            className="text-xs text-orange-600 font-semibold hover:underline flex items-center gap-1"
          >
            <MapPin className="w-3 h-3" /> Show Map
          </button>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 pb-20">
        {/* Sort & Category Bar */}
        <div className="flex items-center justify-between mt-4 mb-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSortBy(sortBy === 'nearest' ? 'rating' : 'nearest')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 hover:border-orange-300 transition-colors"
              data-testid="sort-toggle"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {sortBy === 'nearest' ? 'Nearest First' : 'Top Rated'}
            </button>
          </div>
          <p className="text-xs text-slate-500" data-testid="pandit-count">
            {loading ? 'Searching...' : `${totalFound} pandits found`}
          </p>
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-hide -mx-1 px-1">
          <button
            onClick={() => setActiveCategory('')}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
              !activeCategory ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
            }`}
          >
            All
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.name}
              onClick={() => setActiveCategory(activeCategory === cat.name ? '' : cat.name)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1 ${
                activeCategory === cat.name ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-200 hover:border-orange-300'
              }`}
              data-testid={`cat-chip-${cat.name.replace(/\//g, '-').toLowerCase()}`}
            >
              <span className="text-sm">{cat.icon}</span>
              {cat.name.replace('Vivah/', '')}
            </button>
          ))}
        </div>

        {/* Pandit Cards */}
        <div className="space-y-3 mt-1" ref={listRef}>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-16 h-16 bg-orange-100 rounded-xl flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-100 rounded w-40 mb-2" />
                    <div className="h-3 bg-slate-50 rounded w-28 mb-2" />
                    <div className="h-3 bg-slate-50 rounded w-20" />
                  </div>
                </div>
              </div>
            ))
          ) : displayPandits.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto bg-orange-50 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">No pandits found nearby</h3>
              <p className="text-sm text-slate-500 mb-4">Try increasing the search radius or search a different city</p>
              {locationStatus === 'found' && (
                <button
                  onClick={() => setRadius(r => Math.min(r + 100, 500))}
                  className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-xl hover:bg-orange-600"
                >
                  Expand Search to {Math.min(radius + 100, 500)} km
                </button>
              )}
            </div>
          ) : (
            displayPandits.map((p) => (
              <div
                key={p.id}
                id={`pandit-card-${p.id}`}
                onClick={() => navigate(`/pandit/${p.id}`)}
                className={`bg-white rounded-2xl border shadow-sm cursor-pointer transition-all hover:shadow-md active:scale-[0.99] ${
                  selectedPandit?.id === p.id ? 'border-orange-400 ring-1 ring-orange-200' : 'border-stone-100'
                }`}
                data-testid={`pandit-card-${p.id}`}
              >
                <div className="p-4">
                  <div className="flex gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <img
                        src={p.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=FF9933&color=fff&size=80`}
                        alt={p.name}
                        className="w-16 h-16 rounded-xl object-cover"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div className="min-w-0">
                          <h3 className="font-bold text-slate-800 text-sm truncate flex items-center gap-1">
                            {p.name}
                            {(p.verified) && <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                          </h3>
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{p.city}{p.state ? `, ${p.state}` : ''}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-0.5 bg-green-50 text-green-700 px-2 py-0.5 rounded-lg text-xs font-bold flex-shrink-0 ml-2">
                          <Star className="w-3 h-3 fill-green-600" />
                          {p.rating?.toFixed?.(1) || p.rating}
                        </div>
                      </div>

                      {/* Distance + Availability */}
                      <div className="flex items-center gap-2 flex-wrap mt-1.5">
                        {p.distance_km !== undefined && (
                          <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md" data-testid="distance-badge">
                            {p.distance_km} km away
                          </span>
                        )}
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${
                          (p.availability === 'available') 
                            ? 'text-green-700 bg-green-50' 
                            : 'text-slate-500 bg-slate-50'
                        }`} data-testid="availability-badge">
                          {(p.availability === 'available') ? 'Available Today' : 'Busy'}
                        </span>
                        {p.instant_booking && (
                          <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md flex items-center gap-0.5">
                            <Zap className="w-3 h-3" />
                            Instant
                          </span>
                        )}
                      </div>

                      {/* Meta */}
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                        <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {p.experience_years} yrs</span>
                        <span className="flex items-center gap-0.5"><Award className="w-3 h-3" /> {p.qualification}</span>
                        {p.total_reviews > 0 && <span>{p.total_reviews} reviews</span>}
                      </div>
                    </div>
                  </div>

                  {/* Puja types */}
                  {p.puja_types?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {(p.puja_types || []).slice(0, 3).map(t => (
                        <span key={t} className="text-[10px] px-2 py-0.5 bg-orange-50 text-orange-700 rounded-md font-medium">{t}</span>
                      ))}
                      {p.puja_types.length > 3 && (
                        <span className="text-[10px] px-2 py-0.5 bg-slate-50 text-slate-500 rounded-md">+{p.puja_types.length - 3} more</span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-50">
                    <div>
                      <span className="text-base font-bold text-orange-600">
                        ₹{(p.fees_range_min || p.fees_min || 0).toLocaleString('en-IN')}
                      </span>
                      <span className="text-xs text-slate-400"> - ₹{(p.fees_range_max || p.fees_max || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/pandit/${p.id}`); }}
                      className="flex items-center gap-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-all active:scale-95"
                      data-testid={`book-pandit-${p.id}`}
                    >
                      Book Now <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
